<template>
	<div class="positions-panel">
		<div class="positions-panel__title">
			当前持仓
			<span v-if="positions.length" class="positions-panel__count">{{ positions.length }}</span>
		</div>
		<div v-if="positions.length === 0" class="positions-panel__empty">
			<span class="positions-panel__empty-icon">◇</span>
			<span>暂无持仓</span>
			<span class="positions-panel__empty-hint">在交易面板做多或做空后，将在此显示</span>
		</div>
		<div v-else class="positions-panel__list">
			<div v-for="position in positions" :key="position.id" class="position-card" :class="position.pnlClass">
				<div class="position-card__row">
					<span class="position-card__symbol">{{ position.symbol }}</span>
					<span class="position-card__direction" :class="position.direction === 'long' ? 'is-long' : 'is-short'">
						{{ fmtDirection(position.direction) }} {{ position.leverage }}×
					</span>
					<span class="position-card__pnl" :class="position.pnlClass">{{ fmtSignedMoney(position.pnlCents) }}</span>
				</div>
				<div class="position-card__row">
					<span class="position-card__item">开仓 {{ fmtPrice(position.openPrice) }}</span>
					<span class="position-card__item">现价 {{ fmtPrice(position.currentPrice) }}</span>
					<span class="position-card__item">保证金 {{ fmtMoney(position.marginCents) }}</span>
					<span class="position-card__item">可承受反向波动 {{ fmtPercent(1 / position.leverage) }}</span>
				</div>
				<button class="position-card__close" :disabled="disabled" @click="$emit('close', position.id)">
					平仓
				</button>
			</div>
		</div>
	</div>
</template>

<script>
import { fmtDirection, fmtMoney, fmtPercent, fmtPrice, fmtSignedMoney } from '@/util/fx/format'

export default {
	name: 'PositionsPanel',
	props: {
		positions: {
			type: Array,
			default: () => []
		},
		disabled: {
			type: Boolean,
			default: false
		}
	},
	methods: {
		fmtDirection,
		fmtMoney,
		fmtPercent,
		fmtPrice,
		fmtSignedMoney
	}
}
</script>

<style scoped>
.positions-panel {
	height: 100%;
	box-sizing: border-box;
	background: linear-gradient(180deg, var(--fx-panel-2), var(--fx-panel));
	border: 1px solid var(--fx-border);
	border-radius: 12px;
	padding: 14px;
	min-height: 0;
	overflow-y: auto;
}

.positions-panel__title {
	display: flex;
	align-items: center;
	gap: 8px;
	font-size: 13px;
	font-weight: 600;
	color: var(--fx-text);
	letter-spacing: 0.04em;
	margin-bottom: 10px;
}

.positions-panel__count {
	display: inline-flex;
	align-items: center;
	justify-content: center;
	min-width: 18px;
	height: 18px;
	padding: 0 5px;
	border-radius: 999px;
	background: var(--fx-accent-soft);
	border: 1px solid rgba(77, 162, 255, 0.4);
	color: #9cc7ff;
	font-size: 12px;
	font-weight: 700;
	font-family: var(--fx-mono);
	box-sizing: border-box;
}

.positions-panel__empty {
	display: flex;
	flex-direction: column;
	align-items: center;
	justify-content: center;
	gap: 6px;
	margin: 12px 0;
	padding: 22px 16px;
	border: 1px dashed var(--fx-border);
	border-radius: 10px;
	font-size: 14px;
	color: var(--fx-text-dim);
	text-align: center;
}

.positions-panel__empty-icon {
	font-size: 20px;
	color: var(--fx-text-faint);
	line-height: 1;
}

.positions-panel__empty-hint {
	font-size: 12.5px;
	color: var(--fx-text-faint);
}

.positions-panel__list {
	display: flex;
	flex-direction: column;
	gap: 8px;
}

.position-card {
	position: relative;
	background: linear-gradient(180deg, rgba(255, 255, 255, 0.045), rgba(255, 255, 255, 0.025));
	border: 1px solid var(--fx-border);
	border-left-width: 3px;
	border-radius: 8px;
	padding: 10px 10px 10px 14px;
	display: flex;
	flex-direction: column;
	gap: 6px;
	transition: border-color 0.18s, transform 0.18s, box-shadow 0.18s;
}

.position-card:hover {
	border-color: var(--fx-border-strong);
	transform: translateY(-1px);
	box-shadow: 0 6px 18px rgba(0, 0, 0, 0.25);
}

.position-card.is-up {
	border-left-color: var(--fx-up);
}

.position-card.is-down {
	border-left-color: var(--fx-down);
}

.position-card__row {
	display: flex;
	flex-wrap: wrap;
	align-items: center;
	gap: 10px;
}

.position-card__row:first-child {
	padding-right: 62px;
}

.position-card__symbol {
	font-size: 14px;
	font-weight: 600;
	color: var(--fx-text);
}

.position-card__direction {
	display: inline-flex;
	align-items: center;
	font-size: 13px;
	padding: 2px 8px;
	border-radius: 999px;
}

.position-card__direction.is-long {
	background: var(--fx-up-soft);
	color: #ff8d84;
}

.position-card__direction.is-short {
	background: var(--fx-down-soft);
	color: #43d9a0;
}

.position-card__direction.is-long::before {
	content: '▲';
	font-size: 10px;
	margin-right: 4px;
}

.position-card__direction.is-short::before {
	content: '▼';
	font-size: 10px;
	margin-right: 4px;
}

.position-card__pnl {
	font-size: 14px;
	font-weight: 700;
	font-family: var(--fx-mono);
	font-variant-numeric: tabular-nums;
	margin-left: auto;
}

.position-card__pnl.is-up {
	color: var(--fx-up);
}

.position-card__pnl.is-down {
	color: var(--fx-down);
}

.position-card__item {
	font-size: 13px;
	color: var(--fx-text-dim);
	font-family: var(--fx-mono);
}

.position-card__close {
	position: absolute;
	top: 10px;
	right: 10px;
	padding: 3px 11px;
	border-radius: 7px;
	border: 1px solid var(--fx-border);
	background: rgba(255, 255, 255, 0.04);
	color: var(--fx-text-dim);
	font-size: 13px;
	cursor: pointer;
	transition: border-color 0.15s, color 0.15s, background 0.15s;
}

.position-card__close:hover:not(:disabled) {
	border-color: rgba(77, 162, 255, 0.5);
	background: var(--fx-accent-soft);
	color: #9cc7ff;
}

.position-card__close:disabled {
	opacity: 0.35;
	cursor: not-allowed;
}

.positions-panel::-webkit-scrollbar {
	width: 8px;
}

.positions-panel::-webkit-scrollbar-thumb {
	background: rgba(148, 170, 220, 0.16);
	border-radius: 999px;
}

.positions-panel::-webkit-scrollbar-thumb:hover {
	background: rgba(148, 170, 220, 0.28);
}
</style>
